import React, { useState, useEffect, useMemo } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Plus, Pencil, Trash2, UserCircle, Search, KeyRound } from 'lucide-react';
import { toast } from 'sonner';
import { CargaMasivaButton } from '../components/CargaMasivaButton';

export const ConductoresPage = () => {
  const [conductores, setConductores] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [currentConductor, setCurrentConductor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [newPasswordValue, setNewPasswordValue] = useState('');
  const [useDefaultPwd, setUseDefaultPwd] = useState(true);
  const [forceChange, setForceChange] = useState(true);
  const [pwdLoading, setPwdLoading] = useState(false);
  const [formData, setFormData] = useState({
    nombre: '',
    rut: '',
    telefono: '',
    email: ''
  });

  // Filtrar conductores basado en el término de búsqueda
  const filteredConductores = useMemo(() => {
    if (!searchTerm.trim()) return conductores;
    const term = searchTerm.toLowerCase();
    return conductores.filter(conductor => 
      conductor.nombre?.toLowerCase().includes(term) ||
      conductor.rut?.toLowerCase().includes(term) ||
      conductor.telefono?.toLowerCase().includes(term) ||
      conductor.email?.toLowerCase().includes(term)
    );
  }, [conductores, searchTerm]);

  useEffect(() => {
    loadConductores();
  }, []);

  const loadConductores = async () => {
    try {
      const response = await api.get('/conductores');
      setConductores(response.data);
    } catch (error) {
      toast.error('Error al cargar conductores');
    }
  };

  const handleOpenDialog = (conductor = null) => {
    if (conductor) {
      setCurrentConductor(conductor);
      setFormData({
        nombre: conductor.nombre,
        rut: conductor.rut,
        telefono: conductor.telefono,
        email: conductor.email || ''
      });
    } else {
      setCurrentConductor(null);
      setFormData({ nombre: '', rut: '', telefono: '', email: '' });
    }
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (currentConductor) {
        await api.put(`/conductores/${currentConductor.id}`, formData);
        toast.success('Conductor actualizado');
      } else {
        await api.post('/conductores', formData);
        toast.success('Conductor creado');
      }
      await loadConductores();
      setShowDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar conductor');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await api.delete(`/conductores/${currentConductor.id}`);
      toast.success('Conductor eliminado');
      await loadConductores();
      setShowDeleteDialog(false);
    } catch (error) {
      toast.error('Error al eliminar conductor');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenPasswordDialog = (conductor) => {
    setCurrentConductor(conductor);
    setNewPasswordValue('');
    setUseDefaultPwd(true);
    setForceChange(true);
    setShowPasswordDialog(true);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!useDefaultPwd && newPasswordValue.trim().length < 4) {
      toast.error('La contraseña debe tener al menos 4 caracteres');
      return;
    }
    setPwdLoading(true);
    try {
      const payload = {
        new_password: useDefaultPwd ? null : newPasswordValue.trim(),
        force_change: forceChange,
      };
      const res = await api.post(`/conductores/${currentConductor.id}/reset-password`, payload);
      if (res.data?.default_password_used) {
        toast.success('Contraseña restablecida a los primeros 5 dígitos del RUT');
      } else {
        toast.success('Contraseña del conductor actualizada');
      }
      setShowPasswordDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar la contraseña');
    } finally {
      setPwdLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="conductores-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope'] text-gray-900">Conductores</h1>
          <p className="text-gray-600 mt-1">Gestión de conductores</p>
        </div>
        <div className="flex gap-2">
          <CargaMasivaButton
            endpoint="/conductores/carga-masiva"
            onSuccess={loadConductores}
            templateColumns={['nombre', 'rut', 'telefono', 'email']}
            templateName="conductores"
            entityName="Conductores"
          />
          <Button onClick={() => handleOpenDialog()} data-testid="create-conductor-button">
            <Plus size={18} className="mr-2" />
            Nuevo Conductor
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-6">
          {/* Buscador */}
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <Input
                placeholder="Buscar por nombre, RUT, teléfono o email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-conductores-input"
              />
            </div>
            {searchTerm && (
              <p className="text-sm text-gray-500 mt-2">
                {filteredConductores.length} resultado{filteredConductores.length !== 1 ? 's' : ''} encontrado{filteredConductores.length !== 1 ? 's' : ''}
              </p>
            )}
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nombre</TableHead>
                <TableHead>RUT</TableHead>
                <TableHead>Teléfono</TableHead>
                <TableHead>Email</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredConductores.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-gray-500 py-8">
                    {searchTerm ? 'No se encontraron conductores con ese criterio' : 'No hay conductores registrados'}
                  </TableCell>
                </TableRow>
              ) : (
                filteredConductores.map((conductor) => (
                  <TableRow key={conductor.id}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <UserCircle size={16} className="text-primary" />
                        {conductor.nombre}
                      </div>
                    </TableCell>
                    <TableCell>{conductor.rut}</TableCell>
                    <TableCell>{conductor.telefono}</TableCell>
                    <TableCell>{conductor.email || '-'}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleOpenPasswordDialog(conductor)}
                          title="Modificar contraseña"
                          data-testid={`reset-password-conductor-${conductor.id}`}
                        >
                          <KeyRound size={16} className="text-amber-600" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleOpenDialog(conductor)}
                        >
                          <Pencil size={16} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            setCurrentConductor(conductor);
                            setShowDeleteDialog(true);
                          }}
                        >
                          <Trash2 size={16} className="text-red-600" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent data-testid="conductor-dialog">
          <DialogHeader>
            <DialogTitle>
              {currentConductor ? 'Editar Conductor' : 'Nuevo Conductor'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="nombre">Nombre *</Label>
              <Input
                id="nombre"
                value={formData.nombre}
                onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                required
                data-testid="nombre-input"
              />
            </div>

            <div>
              <Label htmlFor="rut">RUT *</Label>
              <Input
                id="rut"
                value={formData.rut}
                onChange={(e) => setFormData({ ...formData, rut: e.target.value })}
                required
                placeholder="12.345.678-9"
                data-testid="rut-input"
              />
            </div>

            <div>
              <Label htmlFor="telefono">Teléfono *</Label>
              <Input
                id="telefono"
                type="tel"
                value={formData.telefono}
                onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                required
                placeholder="+56 9 1234 5678"
                data-testid="telefono-input"
              />
            </div>

            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                data-testid="email-input"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={loading} data-testid="submit-conductor-button">
                {loading ? 'Guardando...' : 'Guardar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent data-testid="conductor-password-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound size={20} className="text-amber-600" />
              Modificar contraseña
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleResetPassword} className="space-y-4">
            <div className="bg-gray-50 border border-gray-200 rounded-md p-3 text-sm">
              <p className="text-gray-600">Conductor</p>
              <p className="font-semibold text-gray-900">{currentConductor?.nombre}</p>
              <p className="text-xs text-gray-500">{currentConductor?.rut}</p>
            </div>

            <div className="space-y-2">
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="radio"
                  checked={useDefaultPwd}
                  onChange={() => setUseDefaultPwd(true)}
                  className="mt-1"
                  data-testid="pwd-mode-default"
                />
                <div>
                  <p className="text-sm font-medium text-gray-900">Restablecer al valor por defecto</p>
                  <p className="text-xs text-gray-500">Se usarán los <strong>primeros 5 dígitos del RUT</strong> como contraseña.</p>
                </div>
              </label>
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="radio"
                  checked={!useDefaultPwd}
                  onChange={() => setUseDefaultPwd(false)}
                  className="mt-1"
                  data-testid="pwd-mode-custom"
                />
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">Asignar una contraseña personalizada</p>
                </div>
              </label>
            </div>

            {!useDefaultPwd && (
              <div>
                <Label htmlFor="newPwd">Nueva contraseña</Label>
                <Input
                  id="newPwd"
                  type="text"
                  value={newPasswordValue}
                  onChange={(e) => setNewPasswordValue(e.target.value)}
                  placeholder="Mínimo 4 caracteres"
                  autoComplete="off"
                  data-testid="new-pwd-input"
                />
              </div>
            )}

            <label className="flex items-center gap-2 cursor-pointer pt-2 border-t border-gray-100">
              <input
                type="checkbox"
                checked={forceChange}
                onChange={(e) => setForceChange(e.target.checked)}
                data-testid="force-change-checkbox"
              />
              <span className="text-sm text-gray-700">Forzar al conductor a cambiar la contraseña en su próximo ingreso</span>
            </label>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowPasswordDialog(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={pwdLoading} data-testid="submit-reset-pwd">
                {pwdLoading ? 'Guardando...' : 'Guardar contraseña'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Eliminación</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Estás seguro de eliminar al conductor <strong>{currentConductor?.nombre}</strong>?
              Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              disabled={loading}
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};